import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api, API_BASE_URL, getAccessToken } from '../../lib/api';
import { SignInPage } from '../SignInPage';
import './live.css';

/**
 * Presenter console for a live talk (/live/:code/host). The deck drives the phases on its own as the
 * slides advance; this page is the manual override: jump to any phase, reveal a round, hide a word,
 * reset after a rehearsal.
 */
const PHASES: Array<[string, string, Record<string, unknown>?]> = [
  ['lobby', 'Lobby'], ['survey', 'Survey open'], ['results', 'Survey closed'], ['predict', 'Next-word game'],
  ['quiz', 'Round 1', { round: 'r1' }], ['quiz', 'Round 2', { round: 'r2' }], ['quiz', 'Round 3', { round: 'r3' }], ['quiz', 'Round 4', { round: 'r4' }],
  ['redact', 'Take the details out'], ['report', 'Report'], ['group', 'This group, today'], ['signup', 'End (to techedify.com)'],
];

export function HostPage() {
  const { status } = useAuth();
  const code = (useParams().code || '').toLowerCase();
  const [t, setT] = useState<any>(null);
  const [denied, setDenied] = useState(false);
  // box-api only answers presenters (TALK_VIEWERS); anyone else gets 403 and a notice.
  const load = useCallback(() => api.get(`/box/live-host/${code}/tally`).then(setT).catch((e: { status?: number }) => { if (e?.status === 403) setDenied(true); }), [code]);

  useEffect(() => {
    if (status !== 'authenticated') return;
    load();
    const ws = new WebSocket(`${API_BASE_URL.replace(/^http/, 'ws')}/box/live/ws?code=${code}&token=${encodeURIComponent(getAccessToken() || '')}`);
    ws.onmessage = ev => { const m = JSON.parse(ev.data); if (m.t === 'tally') setT(m); };
    return () => ws.close();
  }, [code, load, status]);

  if (status === 'loading') return null;
  if (status === 'anonymous') return <SignInPage />;
  if (denied) return <div className="lv-root"><main className="lv-main"><section className="lv-card lv-center"><h1 className="lv-h">This console is private.</h1><p className="lv-lead">Only the presenter's account can open it.</p></section></main></div>;
  const control = (phase: string, extra: Record<string, unknown> = {}) => api.post(`/box/live-host/${code}/control`, { phase, ...extra }).then(load);
  const st = t?.state;

  return (
    <div className="lv-root"><main className="lv-main" style={{ maxWidth: 900 }}>
      <section className="lv-card">
        <p className="lv-kicker">Live session · {code}</p>
        <h1 className="lv-h">{t ? `${t.n} answered · ${t.signups} signed up` : 'Loading…'}</h1>
        <p className="lv-lead">Now: <b>{st?.phase}{st?.round ? ` · ${st.round}` : ''}{st?.revealed ? ' (revealed)' : ''}</b>. Audience link: <b>{window.location.origin}/live/{code}</b></p>
        <div className="lv-opts grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
          {PHASES.map(([p, label, extra]) => {
            const on = st?.phase === p && (!extra || st?.round === extra.round);
            return <button key={label} className={`lv-opt center ${on ? 'on' : ''}`} onClick={() => control(p, extra)}>{label}</button>;
          })}
        </div>
        {st?.phase === 'quiz' && <button className="lv-primary" onClick={() => control('quiz', { round: st.round, revealed: !st.revealed })}>{st.revealed ? 'Hide answer' : 'Reveal answer'}</button>}
      </section>
      {t && (
        <section className="lv-card">
          <p className="lv-kicker">Words (tap to hide)</p>
          <div className="lv-opts row">{Object.entries(t.words as Record<string, number>).map(([w, c]) => <button key={w} className="lv-opt center" onClick={() => api.post(`/box/live-host/${code}/hide-word`, { word: w }).then(load)}>{w} · {c}</button>)}</div>
        </section>
      )}
      <section className="lv-card">
        <p className="lv-kicker">Session {st?.run ?? 1}</p>
        <button className="lv-primary" onClick={() => { if (window.confirm('Archive every answer, vote and sign-up in this session and start fresh? Nothing is deleted; devices start over.')) api.post(`/box/live-host/${code}/archive`, {}).then(load); }}>Archive and start a fresh session</button>
        <p className="lv-kicker" style={{ marginTop: 18 }}>Rehearsal</p>
        <button className="lv-primary" style={{ background: '#B5281F' }} onClick={() => { if (window.confirm('Delete every answer and vote in this session? Sign-ups are kept.')) api.post(`/box/live-host/${code}/reset`, {}).then(load); }}>Reset answers and votes</button>
      </section>
    </main></div>
  );
}
